"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import "./insights.css";

// Reveals an element with a fade + gentle upward drift the first time it
// scrolls into view. No left/right movement — only opacity + translateY.
function useReveal<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    if (typeof IntersectionObserver === "undefined") {
      setVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setVisible(true);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.15, rootMargin: "0px 0px -60px 0px" }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return { ref, visible };
}

// Remote image URLs are kept in one place, following the same pattern as the
// What We Do page you shared. Replace any URL later without touching the JSX.
const imageUrls = {
  hero: "/assets/images/insights-banner.jpg",
  featuredAI: "/assets/images/2.jpg",
  businessAI: "/assets/images/5.jpg",
  blueprint: "/assets/images/6.jpg",
  machinesLearn: "/assets/images/7.jpg",
  airlineSeat: "/assets/images/8.jpg",
  aiMaths: "/assets/images/9.jpg",
  aiGpu: "/assets/images/10.jpg",
  studentTeacher: "/assets/images/insights.jpg",
  enterpriseGuide: "/assets/images/whitepaper.jpg",
  gaugeAssignment: "/assets/images/4.jpg",
  greenAI: "/assets/images/case-study.jpg",
  cta: "/assets/images/3.jpg",
};

type IconName =
  | "bulb"
  | "document"
  | "ai"
  | "database"
  | "network"
  | "chart"
  | "people"
  | "book";

function Icon({ name }: { name: IconName }) {
  const common = {
    width: 30,
    height: 30,
    viewBox: "0 0 30 30",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    "aria-hidden": true,
  };

  const paths: Record<IconName, ReactNode> = {
    bulb: (
      <>
        <path d="M15 3.5a7 7 0 0 0-4.25 12.56c.94.72 1.5 1.82 1.5 3.01h5.5c0-1.19.56-2.29 1.5-3.01A7 7 0 0 0 15 3.5Z" />
        <path d="M12.5 22h5M13.5 25h3M15 1v-0.2M4.6 5.1l-.9-.9M25.4 5.1l.9-.9M2.7 13H1.5M28.5 13h-1.2" />
      </>
    ),
    document: (
      <>
        <rect x="6.5" y="3.5" width="17" height="23" rx="1.8" />
        <path d="M11 9h8M11 13h8M11 17h5M19.5 3.8v4h4" />
        <path d="m16.7 21.2 1.4 1.4 2.8-3" />
      </>
    ),
    ai: (
      <>
        <rect x="6" y="7" width="18" height="16" rx="2" />
        <path d="M10 4v3M15 4v3M20 4v3M10 23v3M15 23v3M20 23v3M3 11h3M3 16h3M24 11h3M24 16h3" />
        <path d="M10.5 17v-4l2.2 2 2.3-3 2.3 3 2.2-2v4" />
      </>
    ),
    database: (
      <>
        <ellipse cx="15" cy="6.5" rx="8.5" ry="3.5" />
        <path d="M6.5 6.5v7c0 1.94 3.8 3.5 8.5 3.5s8.5-1.56 8.5-3.5v-7M6.5 13.5v7c0 1.94 3.8 3.5 8.5 3.5s8.5-1.56 8.5-3.5v-7" />
      </>
    ),
    network: (
      <>
        <circle cx="15" cy="6" r="3" />
        <circle cx="7" cy="14" r="3" />
        <circle cx="23" cy="14" r="3" />
        <circle cx="11" cy="24" r="3" />
        <circle cx="19" cy="24" r="3" />
        <path d="m12.8 8.2-3.7 3.7M17.2 8.2l3.7 3.7M8.6 16.4l1.9 4.8M21.4 16.4l-1.9 4.8M10 14h10" />
      </>
    ),
    chart: (
      <>
        <path d="M5 25V15M11 25V10M17 25V13M23 25V5" />
        <path d="m4 10 6-4 6 3 8-7" />
        <path d="M20 2h4v4" />
      </>
    ),
    people: (
      <>
        <circle cx="15" cy="8" r="3.5" />
        <circle cx="6.5" cy="11" r="2.7" />
        <circle cx="23.5" cy="11" r="2.7" />
        <path d="M8.5 24c.4-4 2.5-6 6.5-6s6.1 2 6.5 6M2.5 23c.25-2.9 1.65-4.5 4-4.5 1.3 0 2.3.4 3.1 1.2M27.5 23c-.25-2.9-1.65-4.5-4-4.5-1.3 0-2.3.4-3.1 1.2" />
      </>
    ),
    book: (
      <>
        <path d="M4.5 5.5c3.5-.8 7.2.2 10.5 2.5v17c-3.3-2.3-7-3.3-10.5-2.5v-17ZM25.5 5.5c-3.5-.8-7.2.2-10.5 2.5v17c3.3-2.3 7-3.3-10.5-2.5v-17Z" />
      </>
    ),
  };

  return (
    <svg {...common} className="orane-insights-page__insight-icon">
      <g stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round">
        {paths[name]}
      </g>
    </svg>
  );
}

function Arrow({ small = false }: { small?: boolean }) {
  return (
    <span className={small ? "orane-insights-page__arrow-circle orane-insights-page__arrow-circle--small" : "orane-insights-page__arrow-circle"} aria-hidden="true">
      <svg viewBox="0 0 20 20" fill="none">
        <path d="M4 10h11M10.5 5.5 15 10l-4.5 4.5" />
      </svg>
    </span>
  );
}

/* [icon, title, description, href] */
const topicCards = [
  ["bulb", "This Week at Orane", "Latest updates, events and highlights.", "/week-at-orane"],
  ["document", "52 Problems", "One real business problem every week.", "/52-problems"],
  ["ai", "Enterprise AI", "Trends, use cases and practical guides.", "/enterprise-ai-intelligence"],
  ["database", "SAP BDC", "Data, semantics and business value.", "/datasap"],
  ["network", "Enterprise Transformation", "People, process, technology and change.", "/enterprise-transformation"],
  ["chart", "Digital Days", "Short reads. Big ideas. Real outcomes.", "/digital-days"],
  ["people", "Industry Perspectives", "Domain insights across sectors.", "/industry-perspectives"],
  ["book", "Research & Thinking", "Reports, whitepapers and deep dives.", "/business-knowledge"],
] as const;

const featuredCards = [
  {
    image: imageUrls.businessAI,
    title: "How Does AI Understand a Business?",
    description: "From coffee shops to data models — understanding the business with AI.",
    meta: "5 min read",
    date: "14 Sep 2026",
  },
  {
    image: imageUrls.blueprint,
    title: "The Enterprise AI Blueprint",
    description: "LLM vs TFM. From questions to multi-dimensional answers.",
    meta: "6 min read",
    date: "12 Sep 2026",
  },
  {
    image: imageUrls.machinesLearn,
    title: "How Machines Learn",
    description: "Linear regression in TFM — from theory to real use.",
    meta: "5 min read",
    date: "10 Sep 2026",
  },
  {
    image: imageUrls.airlineSeat,
    title: "What Is One Airline Seat Really Worth?",
    description: "The power of networks, hubs and dynamic pricing.",
    meta: "6 min read",
    date: "08 Sep 2026",
  },
];

const latestCards = [
  {
    type: "ARTICLES",
    image: imageUrls.aiMaths,
    title: "AI and Maths",
    description: "Why math is the foundation of enterprise AI.",
    meta: "7 min read",
    date: "05 Sep 2026",
  },
  {
    type: "RESEARCH",
    image: imageUrls.aiGpu,
    title: "AI and GPU",
    description: "The economy of tokens vs. the economy of GPU.",
    meta: "6 min read",
    date: "02 Sep 2026",
  },
  {
    type: "INSIGHTS",
    image: imageUrls.studentTeacher,
    title: "Student & Teacher Model",
    description: "How AI is changing learning and work.",
    meta: "5 min read",
    date: "30 Aug 2026",
  },
  {
    type: "WHITEPAPER",
    image: imageUrls.enterpriseGuide,
    title: "The 10-Chapter Guide to Enterprise AI",
    description: "A practical guide for business leaders.",
    meta: "5 min read",
  },
  {
    type: "AIRLINE DEEP DIVE",
    image: imageUrls.gaugeAssignment,
    title: "Dynamic Gauge Assignment",
    description: "2011 vs. today — from manual to AI-driven.",
    meta: "8 min read",
    date: "28 Aug 2026",
  },
  {
    type: "CASE STUDY",
    image: imageUrls.greenAI,
    title: "Sustainability & Green AI",
    description: "Technology for a better tomorrow.",
    meta: "6 min read",
    date: "26 Aug 2026",
  },
];

export default function InsightsPage() {
  const topics$ = useReveal<HTMLDivElement>();
  const focus$ = useReveal<HTMLDivElement>();
  const latest$ = useReveal<HTMLDivElement>();
  const newsletter$ = useReveal<HTMLElement>();

  return (
    <main className="orane-insights-page">

      <section className="orane-insights-page__hero">
        <img src={imageUrls.hero} alt="" className="orane-insights-page__hero-image" />
        <div className="orane-insights-page__hero-overlay" />
        <div className="orane-insights-page__hero-content">
          <h1 className="orane-loadIn" style={{ animationDelay: "0.1s" }}>
            Ideas. Perspectives.
            <br />
            <span>Real Impact.</span>
          </h1>
          <p className="orane-insights-page__hero-copy orane-loadIn" style={{ animationDelay: "0.28s" }}>
            Thought leadership, practical insights, and real-world
            <br className="orane-insights-page__desktop-break" />
            perspectives to help you navigate change, solve
            <br className="orane-insights-page__desktop-break" />
            business problems and create lasting value.
          </p>
        </div>
        {/* <div className="orane-insights-page__hero-script">
          Better
          <br />
          Questions.
          <br />
          Bigger
          <br />
          Possibilities.
        </div> */}
      </section>

      <section className="orane-insights-page__topic-strip orane-insights-page__section-shell">
        <div
          className={`orane-insights-page__topic-grid orane-reveal${topics$.visible ? " orane-inView" : ""}`}
          ref={topics$.ref}
        >
          {topicCards.map(([icon, title, description, href], i) => (
            <div
            
              className="orane-insights-page__topic-card"
              key={title}
              style={{ transitionDelay: topics$.visible ? `${i * 0.05}s` : "0s" }}
            >
              <Icon name={icon} />
              <h3>{title}</h3>
              <p>{description}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="orane-insights-page__section-shell orane-insights-page__focus-section">
        <div className="orane-insights-page__section-heading-row">
          <div>
            <p className="orane-insights-page__eyebrow orane-insights-page__eyebrow--dark">FEATURED INSIGHTS</p>
            <h2>In Focus</h2>
          </div>
          <a className="orane-insights-page__view-all" href="#">
            View All Insights <span>→</span>
          </a>
        </div>

        <div
          className={`orane-insights-page__focus-grid orane-reveal${focus$.visible ? " orane-inView" : ""}`}
          ref={focus$.ref}
        >
          <a href="#" className="orane-insights-page__feature-main">
            <img src={imageUrls.featuredAI} alt="" />
            <div className="orane-insights-page__feature-image-cover" />
            <div className="orane-insights-page__feature-content">
              <span className="orane-insights-page__pill">THE ENTERPRISE AI MAP</span>
              <h3>Part 1/7 – We May Be Learning AI Backwards</h3>
              <p>AI is not the starting point.</p>
              <p>The business is.</p>
              <div className="orane-insights-page__card-meta">
                <span>6 min read</span>
                <i />
                <span>16 Sep 2026</span>
              </div>
            </div>
            {/* <Arrow /> */}
          </a>

          <div className="orane-insights-page__focus-side">
            {featuredCards.map((card) => (
              <a href="#" className="orane-insights-page__mini-focus-card" key={card.title}>
                <div className="orane-insights-page__mini-image">
                  <img src={card.image} alt="" />
                </div>
                <div className="orane-insights-page__mini-copy">
                  <h3>{card.title}</h3>
                  <p>{card.description}</p>
                  <div className="orane-insights-page__card-meta">
                    <span>{card.meta}</span>
                    <i />
                    <span>{card.date}</span>
                  </div>
                </div>
                {/* <Arrow small /> */}
              </a>
            ))}
          </div>
        </div>
      </section>

      <section className="orane-insights-page__section-shell orane-insights-page__latest-section">
        <div className="orane-insights-page__section-heading-row orane-insights-page__latest-heading">
          <div>
            <p className="orane-insights-page__eyebrow orane-insights-page__eyebrow--dark">MORE INSIGHTS</p>
            <h2>Explore Our Latest</h2>
          </div>
        </div>

        <div
          className={`orane-insights-page__latest-grid orane-reveal${latest$.visible ? " orane-inView" : ""}`}
          ref={latest$.ref}
        >
          {latestCards.map((card, i) => (
            <a
              href="#"
              className="orane-insights-page__latest-card"
              key={card.title}
              style={{ transitionDelay: latest$.visible ? `${i * 0.06}s` : "0s" }}
            >
              <div className="orane-insights-page__latest-image">
                <img src={card.image} alt="" />
              </div>
              <div className="orane-insights-page__latest-body">
                <p className="orane-insights-page__card-type">{card.type}</p>
                <h3>{card.title}</h3>
                <p className="orane-insights-page__latest-description">{card.description}</p>

                <div className="orane-insights-page__latest-meta">
                  <span>{card.meta}</span>
                </div>
                <Arrow small />
              </div>
            </a>
          ))}
        </div>
      </section>

      <section
        className={`orane-insights-page__section-shell orane-insights-page__newsletter orane-reveal${newsletter$.visible ? " orane-inView" : ""}`}
        ref={newsletter$.ref}
      >
        <img
          src={imageUrls.cta}
          alt=""
          className="orane-insights-page__newsletter-bg"
        />
        <div className="orane-insights-page__newsletter-copy">
          <p className="orane-insights-page__eyebrow2">STAY AHEAD</p>
          <h2>
            Get the latest insights,
            <br />
            straight to your inbox.
          </h2>
          <p>Thought leadership, industry updates and more — curated for you.</p>
        </div>

      </section>
    </main>
  );
}